import { GraphSchema } from '@ta/server';

import { Company } from '../../company/dto/company';
import { EstateManager } from '../../company/dto/estate-manager';
import { Address } from '../../adresses/dto/address';
import { Media } from '../../medias/dto/media';
import { TenantApplication } from '../../tenant-applications/dto/tenant-application';
import { Owner } from '../../users/dto/owner';
import { ExtraInformation } from './extra-information';

export enum Status {
  Unknown = 0,
  Pending = 1,
  Validated = 2,
  UnderReview = 3,
  Rejected = 4,
}

/** Source de la couverture d'un bien. Un bien offert **et** payé répond `FREE_GRANT`. */
export enum EstateCoverageSource {
  None = 'NONE',
  Subscription = 'SUBSCRIPTION',
  FreeGrant = 'FREE_GRANT',
}

/** Un bien offert : premier bien offert ou geste commercial, le bailleur ne fait pas la différence. */
export interface EstateFreeGrant {
  id: string;
  /** UTC. `null` = cadeau sans limite. */
  expiresAt: string | null;
}

export const estateFreeGrantProps = new GraphSchema<EstateFreeGrant>(['id', 'expiresAt']);

export interface Estate {
  id: string;
  name: string;
  description: string;
  status: Status;
  available: boolean;
  floorArea: number;
  rooms: number;
  bedrooms: number;
  floor: number;
  rent: number;
  serviceCharges: number;
  securityDeposit: number;
  insurance: boolean;
  globalNote: number;
  trustScore?: number;
  createdDate: string;
  updatedDate: string;
  addressId: string;
  firstImageId?: string;
  firstImage?: Media;
  media: Media[];
  address: Address;
  owner?: Owner;
  companyId?: string;
  company?: Company;
  managers?: EstateManager[];
  tenantApplications?: TenantApplication[];
  equipments?: ExtraInformation[];
  /** À `false`, aucun créneau n'est proposé et aucune réservation n'est acceptée. */
  visitsEnabled?: boolean;
  /** Délai minimum entre maintenant et le début d'un créneau réservable. */
  visitMinNoticeHours?: number;
  /** Horizon de publication des créneaux. */
  visitMaxHorizonDays?: number;
  /** Préavis que le locataire doit respecter pour annuler une visite **confirmée**. */
  visitCancellationNoticeHours?: number;
  /** Le bien est couvert à cet instant, par un abonnement ou un accès gratuit. */
  isCovered?: boolean;
  /** UTC. */
  coveredUntil?: string;
  /** `null` si couvert. */
  lockedReason?: string | null;
  /** Pourquoi le bien est couvert : public, c'est lui qui étiquette « Offert » sur une liste. */
  coverageSource?: EstateCoverageSource;
  /** Le cadeau valide du bien ; `null` aussi pour un locataire ou un tiers, jamais une erreur. */
  freeGrant?: EstateFreeGrant | null;
  /** Le bien est en vedette à cet instant. */
  isBoosted?: boolean;
  /** UTC. */
  boostedUntil?: string | null;
  /** Jours entiers restants, arrondis au supérieur. */
  boostDaysLeft?: number;
  /** `null` = le bailleur n'a rien dit : ni autorisation, ni refus. */
  petsAllowed?: boolean | null;
  smokingAllowed?: boolean | null;
  furnished?: boolean | null;
  /** UTC. `null` = disponible immédiatement. */
  availableFrom?: string | null;
  /**
   * L'utilisateur courant a ce bien en favori. Toujours `false` pour un anonyme. Reste `true`
   * sur un bien que « Mes favoris » masque (loué, en modération) : le masquage cache la ligne,
   * il ne retire pas le favori.
   */
  isFavorite?: boolean;
}

export const estateBrutProps: (keyof Estate)[] = [
  'id',
  'name',
  'description',
  'status',
  'available',
  'floorArea',
  'rooms',
  'bedrooms',
  'floor',
  'rent',
  'serviceCharges',
  'securityDeposit',
  'insurance',
  'createdDate',
  'updatedDate',
  'addressId',
  'firstImageId',
  'firstImage',
  'media',
  'address',
  'tenantApplications',
  'owner',
  'companyId',
  'company',
  'managers',
  'equipments',
  'globalNote',
  'trustScore',
  'visitsEnabled',
  'visitMinNoticeHours',
  'visitMaxHorizonDays',
  'visitCancellationNoticeHours',
  'isCovered',
  'coveredUntil',
  'lockedReason',
  'coverageSource',
  'freeGrant',
  'isBoosted',
  'boostedUntil',
  'boostDaysLeft',
  'petsAllowed',
  'smokingAllowed',
  'furnished',
  'availableFrom',
  'isFavorite',
];

export const estateProps = new GraphSchema<Estate>(estateBrutProps);
